import { ListCompanyTransactionsForAdminQuery } from "../api/generated/models/ListCompanyTransactionsForAdminQuery";
import { BusinessAccountTransactionSource } from "../api/generated/models/BusinessAccountTransactionSource";
import {
  dateToCopenhagenStartOfDayUTC,
  dateToCopenhagenEndOfDayUTC,
} from "./dateFilters";

export interface AdminTransactionsFilterDraft {
  startDate: string; // yyyy-mm-dd or ""
  endDate: string; // yyyy-mm-dd or ""
  sources: BusinessAccountTransactionSource[];
  minAmountKr: string; // raw kroner input
  maxAmountKr: string; // raw kroner input
  identity: string; // email or GUID
}

export const emptyFilterDraft: AdminTransactionsFilterDraft = {
  startDate: "",
  endDate: "",
  sources: [],
  minAmountKr: "",
  maxAmountKr: "",
  identity: "",
};

// Convert a kroner input string to integer øre, or undefined when blank/invalid.
export const kronerInputToOre = (input: string): number | undefined => {
  const trimmed = input.trim();
  if (trimmed === "") return undefined;
  const value = Number(trimmed);
  if (!Number.isFinite(value)) return undefined;
  return Math.round(value * 100);
};

interface BuildQueryParams {
  filters: AdminTransactionsFilterDraft;
  pageNumber: number;
  pageSize: number;
  companyId?: string;
  merchantId?: string;
}

export const buildAdminTransactionsQuery = ({
  filters,
  pageNumber,
  pageSize,
  companyId,
  merchantId,
}: BuildQueryParams): ListCompanyTransactionsForAdminQuery => {
  const identity = filters.identity.trim();
  return {
    pagination: { pageNumber, pageSize },
    companyId: companyId ?? undefined,
    merchantId: merchantId ?? undefined,
    from: filters.startDate
      ? dateToCopenhagenStartOfDayUTC(filters.startDate)
      : undefined,
    to: filters.endDate
      ? dateToCopenhagenEndOfDayUTC(filters.endDate)
      : undefined,
    sources: filters.sources.length > 0 ? filters.sources : undefined,
    minAmount: kronerInputToOre(filters.minAmountKr),
    maxAmount: kronerInputToOre(filters.maxAmountKr),
    identity: identity !== "" ? identity : undefined,
  };
};

const VALID_SOURCES = new Set<string>(
  Object.values(BusinessAccountTransactionSource)
);

// Serialize applied filters + page into URL search params.
// Empty values and page 1 are omitted so a pristine page has a clean URL.
export const filtersToSearchParams = (
  filters: AdminTransactionsFilterDraft,
  page: number,
  merchant?: string | null
): URLSearchParams => {
  const params = new URLSearchParams();
  if (filters.startDate) params.set("startDate", filters.startDate);
  if (filters.endDate) params.set("endDate", filters.endDate);
  if (filters.sources.length > 0) params.set("sources", filters.sources.join(","));
  if (filters.minAmountKr.trim() !== "") params.set("minAmount", filters.minAmountKr.trim());
  if (filters.maxAmountKr.trim() !== "") params.set("maxAmount", filters.maxAmountKr.trim());
  if (filters.identity.trim() !== "") params.set("identity", filters.identity.trim());
  if (merchant) params.set("merchant", merchant);
  if (page > 1) params.set("page", String(page));
  return params;
};

// Parse URL search params back into a filter draft + 1-based page.
// Unknown sources are dropped; invalid page falls back to 1.
export const searchParamsToFilters = (
  params: URLSearchParams
): {
  filters: AdminTransactionsFilterDraft;
  page: number;
  merchant: string | null;
} => {
  const sources = (params.get("sources") ?? "")
    .split(",")
    .map((s) => s.trim())
    .filter((s): s is BusinessAccountTransactionSource => VALID_SOURCES.has(s));
  const pageRaw = parseInt(params.get("page") ?? "1", 10);
  return {
    filters: {
      startDate: params.get("startDate") ?? "",
      endDate: params.get("endDate") ?? "",
      sources,
      minAmountKr: params.get("minAmount") ?? "",
      maxAmountKr: params.get("maxAmount") ?? "",
      identity: params.get("identity") ?? "",
    },
    page: Number.isFinite(pageRaw) && pageRaw >= 1 ? pageRaw : 1,
    // Deep-link only: a single partner (Contentful business id) from a link.
    merchant: params.get("merchant") || null,
  };
};
